// © Thibault Sabathier, 2026. frontend/src/components/messaging/MessageAttachments.js
//
// Rendu des pièces jointes DANS une bulle de message.
//
// Sert les DEUX contextes via la prop `scope` : messagerie privée et groupes
// de discussion. Ils n'ont ni la même route ni la même règle d'autorisation —
// appartenance à la conversation d'un côté, `canReadGroup` de l'autre — mais
// exactement le même rendu.
//
// ── POURQUOI UN `<img src>` DIRECT SUR UN ENDPOINT AUTHENTIFIÉ ────────────
// Le fichier est servi par `/api/{messaging|discussions}/attachments/:id`,
// derrière l'auth.
// Un `<img>` ne peut pas porter d'en-tête `Authorization`, donc tout repose
// sur le cookie httpOnly. Vérifié : sur un domaine tenant l'API est
// same-origin (`${window.location.origin}/api`), le cookie part donc seul ;
// sur le domaine central, `app.terrilink.io` -> `api.terrilink.io` reste
// same-site sous `terrilink.io`, ce que `SameSite=Lax` autorise. Aucun
// pré-téléchargement en blob n'est donc nécessaire — ce qui évite d'avoir à
// gérer des ObjectURL ici.
//
// ── POURQUOI RÉSERVER LA PLACE ────────────────────────────────────────────
// `width`/`height` viennent de la base. Sans `aspect-ratio`, chaque image qui
// arrive pousse le fil de discussion vers le bas et le lecteur perd sa ligne —
// le défaut de mise en page le plus désagréable d'une messagerie.

import React, { useState } from 'react'
import PropTypes from 'prop-types'
import CIcon from '@coreui/icons-react'
import { cilFile, cilWarning, cilCloudDownload } from '@coreui/icons'
import { useTranslation } from 'react-i18next'
import { getApiUrl } from '../../utils/apiClient'
import { attachmentPath, formatFileSize } from '../../utils/messageAttachments'

// Le SCOPE décide de l'endpoint : messagerie privée et groupes n'ont ni la
// même route ni la même règle d'autorisation. Passer le mauvais rendrait 404,
// l'identifiant ne correspondant à rien dans l'autre table.
const urlFor = (attachment, scope) => `${getApiUrl()}${attachmentPath(attachment.id, scope)}`

const MessageAttachments = ({ attachments, onOpenImage, scope = 'messaging' }) => {
  const { t } = useTranslation()
  // Un fichier peut avoir disparu du stockage (nettoyage, incident) alors que
  // sa ligne subsiste : l'endpoint répond 404 et l'image resterait cassée.
  const [broken, setBroken] = useState({})

  const list = Array.isArray(attachments) ? attachments : []
  if (list.length === 0) return null

  const images = list.filter((a) => a.attachment_type === 'image')
  const documents = list.filter((a) => a.attachment_type !== 'image')

  return (
    <div className="message-attachments">
      {images.length > 0 && (
        <div
          className={`message-attachments__images message-attachments__images--${Math.min(images.length, 3)}`}
        >
          {images.map((attachment, index) =>
            broken[attachment.id] ? (
              <div key={attachment.id} className="message-attachment-broken">
                <CIcon icon={cilWarning} />
                <span>{t('messaging:attachments.unavailable')}</span>
              </div>
            ) : (
              <button
                key={attachment.id}
                type="button"
                className="message-attachment-image"
                onClick={() => onOpenImage(images.map((a) => urlFor(a, scope)), index)}
                title={t('messaging:attachments.open_image', { name: attachment.original_name })}
                aria-label={t('messaging:attachments.open_image', {
                  name: attachment.original_name,
                })}
                style={
                  // Réservation de la place AVANT chargement. Les dimensions
                  // sont facultatives en base (un GIF peut ne pas être
                  // mesuré) : sans elles, on laisse le flux normal plutôt
                  // qu'un ratio inventé.
                  attachment.width && attachment.height
                    ? { aspectRatio: `${attachment.width} / ${attachment.height}` }
                    : undefined
                }
              >
                <img
                  src={urlFor(attachment, scope)}
                  alt={attachment.original_name}
                  loading="lazy"
                  onError={() => setBroken((prev) => ({ ...prev, [attachment.id]: true }))}
                />
              </button>
            ),
          )}
        </div>
      )}

      {documents.map((attachment) => (
        // Lien natif et non bouton + JS : l'endpoint renvoie déjà
        // `Content-Disposition: attachment` sur un document, donc le
        // navigateur télécharge sans qu'on ait à orchestrer quoi que ce soit.
        <a
          key={attachment.id}
          className="message-attachment-doc"
          href={urlFor(attachment, scope)}
          title={t('messaging:attachments.download', { name: attachment.original_name })}
        >
          <span className="message-attachment-doc__icon">
            <CIcon icon={cilFile} />
          </span>
          <span className="message-attachment-doc__meta">
            <span className="message-attachment-doc__name">{attachment.original_name}</span>
            <span className="message-attachment-doc__size">
              {formatFileSize(attachment.size_bytes)}
            </span>
          </span>
          <span className="message-attachment-doc__action">
            <CIcon icon={cilCloudDownload} />
          </span>
        </a>
      ))}
    </div>
  )
}

MessageAttachments.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      original_name: PropTypes.string,
      mime_type: PropTypes.string,
      size_bytes: PropTypes.number,
      attachment_type: PropTypes.oneOf(['image', 'document']),
      width: PropTypes.number,
      height: PropTypes.number,
    }),
  ),
  onOpenImage: PropTypes.func.isRequired,
  // Littéral aux points d'appel : `attachmentPath` lève sur une valeur
  // inconnue plutôt que de fabriquer une URL silencieusement fausse.
  scope: PropTypes.oneOf(['messaging', 'discussions']),
}

export default MessageAttachments
